/**
 * Offer Variant Bridge v2.0
 * Fully dynamic — reads all config from data-offer-config on .glossi-offer-selector
 * Handles: variant switching, gift injection on ATC, cart gift cleanup, price sync
 */
(function () {
  'use strict';

  /* ─── Helpers ─── */

  function formatMoney(cents) {
    var amount = Number(cents || 0) / 100;
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var locale = (window.Shopify && window.Shopify.locale) || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    } catch (e) {
      return code + ' ' + amount.toFixed(2);
    }
  }

  function qs(root, sel) { return (root || document).querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ─── State ─── */
  var state = {
    config: null,
    product: null,
    selectedOffer: '1',
    sectionId: null,
    booted: false,
    cartFetchPatched: false
  };

  /* ─── DOM finders ─── */

  function getOfferRoot() { return qs(document, '.glossi-offer-selector[data-offer-config]'); }

  function getSectionRoot() {
    if (!state.sectionId) return null;
    return qs(document, '#MainProduct-' + state.sectionId) || qs(document, 'product-info');
  }

  function getProductForm() {
    var root = getSectionRoot();
    if (!root) return qs(document, 'form[action*="/cart/add"]');
    return qs(root, 'product-form form') || qs(root, 'form[action*="/cart/add"]');
  }

  function getVariantInput() {
    var form = getProductForm();
    return form ? qs(form, 'input[name="id"]') : null;
  }

  function getSubmitButton() {
    var root = getSectionRoot();
    return root ? qs(root, '.product-form__submit') : qs(document, '.product-form__submit');
  }

  /* ─── Config ─── */

  function parseConfig() {
    var el = getOfferRoot();
    if (!el) return null;
    try {
      return JSON.parse(el.getAttribute('data-offer-config'));
    } catch (e) {
      console.error('[offer-bridge] Failed to parse data-offer-config', e);
      return null;
    }
  }

  function getOfferByKey(key) {
    if (!state.config || !state.config.offers) return null;
    return state.config.offers.find(function (o) { return String(o.offer) === String(key); }) || null;
  }

  function getOfferByVariantId(vid) {
    if (!state.config || !state.config.offers) return null;
    return state.config.offers.find(function (o) { return String(o.variantId) === String(vid); }) || null;
  }

  /* ─── Product fetch ─── */

  function getProductHandle() {
    var root = getSectionRoot();
    if (root) {
      var url = root.getAttribute('data-url');
      if (url) { var parts = url.split('/'); return parts[parts.length - 1].split('?')[0]; }
    }
    var path = window.location.pathname;
    var match = path.match(/\/products\/([^/?#]+)/);
    if (match) return match[1];
    return null;
  }

  function fetchProduct() {
    var handle = getProductHandle();
    if (!handle) return Promise.reject('no product handle');
    if (state.product) return Promise.resolve(state.product);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) { state.product = p; return p; });
  }

  function variantById(id) {
    if (!state.product) return null;
    return (state.product.variants || []).find(function (v) { return String(v.id) === String(id); }) || null;
  }

  /* ─── Variant switching ─── */

  function switchVariant(variantId) {
    var input = getVariantInput();
    if (!input) return;
    input.value = variantId;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    /* Sync Dawn's variant-selects radio inputs */
    var variant = variantById(variantId);
    if (variant) {
      var title = variant.title || variant.option1;
      var root = getSectionRoot();
      if (root) {
        var radios = qsa(root, 'variant-selects input[type="radio"]');
        radios.forEach(function (r) {
          if (r.value === title) {
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  }

  function updateURL(variantId) {
    if (!variantId) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /* ─── Price sync ─── */

  function syncOfferPrices() {
    var offerRoot = getOfferRoot();
    if (!offerRoot || !state.product || !state.config) return;

    state.config.offers.forEach(function (offer) {
      var variant = variantById(offer.variantId);
      if (!variant) return;

      var option = qs(offerRoot, '[data-offer="' + offer.offer + '"]');
      if (!option) return;

      var priceEl = qs(option, '.glossi-offer-selector__price');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      var compareEl = qs(option, '.glossi-offer-selector__compare');
      if (compareEl) {
        if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      }
    });

    /* Bonus compare-at-prices */
    state.config.offers.forEach(function (offer) {
      if (!offer.gifts) return;
      var bonusContainer = qs(offerRoot, '[data-bonuses-for="' + offer.offer + '"]');
      if (!bonusContainer) return;
      var bonusEls = qsa(bonusContainer, '.glossi-offer-selector__bonus');
      bonusEls.forEach(function (bonusEl, idx) {
        if (offer.gifts[idx] && offer.gifts[idx].comparePrice) {
          var existing = qs(bonusEl, '.glossi-offer-selector__bonus-compare');
          if (!existing) {
            var span = document.createElement('span');
            span.className = 'glossi-offer-selector__bonus-compare';
            span.textContent = formatMoney(offer.gifts[idx].comparePrice);
            bonusEl.appendChild(span);
          }
        }
      });
    });
  }

  function syncMainPrice(variant) {
    if (!variant) return;
    var root = getSectionRoot();
    if (!root) return;
    qsa(root, '.glossi-enhanced-price__current, .price__regular .price-item, .price-item--regular').forEach(function (el) {
      el.textContent = formatMoney(variant.price);
    });
    var hasCompare = variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);
    qsa(root, '.glossi-enhanced-price__compare, .price__sale .price-item--sale').forEach(function (el) {
      if (hasCompare) { el.textContent = formatMoney(variant.compare_at_price); el.style.display = ''; }
      else { el.textContent = ''; el.style.display = 'none'; }
    });
  }

  /* ─── Savings line & guarantee microcopy ─── */

  function ensureSavingsLine() {
    var offerRoot = getOfferRoot();
    if (!offerRoot) return null;
    var el = qs(offerRoot, '.glossi-offer-selector__savings');
    if (!el) {
      el = document.createElement('div');
      el.className = 'glossi-offer-selector__savings';
      el.style.display = 'none';
      var host = qs(offerRoot, '[data-bonuses-for="2"]') || offerRoot;
      host.appendChild(el);
    }
    return el;
  }

  function updateSavings() {
    /* Savings banner removed by design decision (2026-07-23) */
    var el = document.querySelector('.glossi-offer-selector__savings');
    if (el) el.remove();
  }

  function ensureGuaranteeLine() {
    if (!getOfferRoot()) return;
    if (qs(document, '.glossi-offer-selector__guarantee')) return;
    var btn = getSubmitButton();
    if (!btn) return;
    var anchor = btn.closest('.product-form__buttons') || btn;
    var el = document.createElement('div');
    el.className = 'glossi-offer-selector__guarantee';
    el.innerHTML = '<span class="glossi-offer-selector__guarantee-icon" aria-hidden="true">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
      '</span><span class="glossi-offer-selector__guarantee-text"><strong>Less than 1%</strong> of customers claim our 30-Day Money Back Guarantee</span>';
    anchor.insertAdjacentElement('afterend', el);
  }

  /* ─── Offer UI ─── */

  function syncOfferUI(offerKey) {
    var offerRoot = getOfferRoot();
    if (!offerRoot) return;
    state.selectedOffer = String(offerKey);

    qsa(offerRoot, '.glossi-offer-selector__option').forEach(function (opt) {
      var isActive = opt.getAttribute('data-offer') === state.selectedOffer;
      opt.classList.toggle('is-selected', isActive);
      var radio = qs(opt, '.glossi-offer-selector__radio');
      if (radio) radio.checked = isActive;
    });
    /* Bonuses always visible — no hide/show toggle */
  }

  function updateCtaText() {
    var btn = getSubmitButton();
    if (!btn) return;
    var span = btn.querySelector('span') || btn;
    /* Don't override sold-out/unavailable states */
    var current = (span.textContent || '').trim().toLowerCase();
    if (current.indexOf('sold') !== -1 || current.indexOf('unavailable') !== -1) return;
    var offerRoot = getOfferRoot();
    var badge = offerRoot ? qs(offerRoot, '[data-offer="' + state.selectedOffer + '"] .glossi-offer-selector__save-badge') : null;
    var saveText = badge ? badge.textContent.trim() : '';
    span.textContent = saveText ? 'ADD TO CART \u2014 ' + saveText : 'ADD TO CART';
  }

  function selectOffer(offerKey) {
    var offer = getOfferByKey(offerKey);
    if (!offer) return;
    state.selectedOffer = String(offerKey);

    syncOfferUI(offerKey);
    switchVariant(offer.variantId);
    updateURL(offer.variantId);
    updateSavings();
    updateCtaText();

    var variant = variantById(offer.variantId);
    if (variant) {
      syncMainPrice(variant);
      [100, 400].forEach(function (delay) {
        setTimeout(function () { syncMainPrice(variant); }, delay);
      });
    }
  }

  /* ─── Gift injection on ATC ─── */

  function interceptAddToCart() {
    /*
     * Dawn's ProductForm binds its submit handler with .bind(this) in the
     * constructor, so patching the method afterwards has no effect.
     * Instead we use a CAPTURING listener on the form — capturing fires
     * before Dawn's bubbling listener. When we have gifts we call
     * stopImmediatePropagation() to kill Dawn's handler entirely.
     */
    var form = getProductForm();
    if (!form || form.__offerBridgeBound) return;
    form.__offerBridgeBound = true;

    var productFormEl = qs(document, 'product-form');

    form.addEventListener('submit', function (e) {
      var offer = getOfferByKey(state.selectedOffer);
      /* No gifts → let Dawn handle it */
      if (!offer || !offer.gifts || offer.gifts.length === 0) return;

      /* Kill the event so Dawn's handler never fires */
      e.preventDefault();
      e.stopImmediatePropagation();

      var btn = productFormEl ? productFormEl.submitButton : null;
      var spinner = productFormEl ? productFormEl.querySelector('.loading__spinner') : null;
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.classList.add('loading'); }
      if (spinner) spinner.classList.remove('hidden');

      /* Build items array: main product + gifts */
      var items = [{ id: Number(offer.variantId), quantity: 1 }];
      offer.gifts.forEach(function (gift) {
        if (gift.variantId) {
          items.push({
            id: Number(gift.variantId),
            quantity: 1,
            properties: { _gift: 'true', _parent_variant: String(offer.variantId) }
          });
        }
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          /* Open cart drawer or redirect */
          var cartDrawer = qs(document, 'cart-drawer') || qs(document, 'cart-notification');
          if (cartDrawer) {
            /* Remove is-empty so cart content is visible (Dawn normally does this but we blocked its handler) */
            cartDrawer.classList.remove('is-empty');
            fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
              .then(function (r) { return r.json(); })
              .then(function (sections) {
                if (cartDrawer.renderContents) cartDrawer.renderContents({ sections: sections });
              })
              .catch(function () { window.location.href = '/cart'; });
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function (err) {
          console.error('[offer-bridge] ATC failed', err);
        })
        .finally(function () {
          if (btn) { btn.removeAttribute('aria-disabled'); btn.classList.remove('loading'); }
          if (spinner) spinner.classList.add('hidden');
        });
    }, true /* CAPTURE phase — fires before Dawn's bubbling listener */);
  }

  /* ─── Cart cleanup: remove orphan gifts when bundle is removed ─── */

  function cleanupOrphanGifts() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!state.config) return;
        var bundleVariantIds = {};
        state.config.offers.forEach(function (o) {
          if (o.gifts && o.gifts.length > 0) bundleVariantIds[String(o.variantId)] = true;
        });
        var hasBundleInCart = cart.items.some(function (item) { return bundleVariantIds[String(item.variant_id)]; });
        if (hasBundleInCart) return;

        var updates = {};
        cart.items.forEach(function (item) {
          if (item.properties && item.properties._gift === 'true') {
            updates[String(item.key)] = 0;
          }
        });
        if (Object.keys(updates).length === 0) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })
      .catch(function () { /* silent */ });
  }

  function patchFetchForCartCleanup() {
    if (state.cartFetchPatched || !window.fetch) return;
    state.cartFetchPatched = true;
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      return origFetch.apply(this, args).then(function (response) {
        try {
          var url = String(args[0] && args[0].url ? args[0].url : args[0]);
          if (/\/cart\/(change|update)/.test(url)) {
            setTimeout(cleanupOrphanGifts, 500);
          }
        } catch (ex) { /* silent */ }
        return response;
      });
    };
  }

  /* ─── Boot ─── */

  function boot() {
    if (state.booted) return;
    var offerRoot = getOfferRoot();
    if (!offerRoot) return;

    state.config = parseConfig();
    if (!state.config) return;
    state.sectionId = offerRoot.getAttribute('data-section-id');
    state.booted = true;

    patchFetchForCartCleanup();

    qsa(offerRoot, '.glossi-offer-selector__option').forEach(function (opt) {
      opt.addEventListener('click', function () {
        selectOffer(opt.getAttribute('data-offer'));
      });
    });

    fetchProduct().then(function () {
      syncOfferPrices();
      /* CVR: default to offer 2 (Buy 1 Get 1 Free) regardless of ?variant= in ad links */
      var initialOffer = getOfferByKey('2');
      if (!initialOffer) {
        var urlVariant = new URL(window.location.href).searchParams.get('variant');
        initialOffer = urlVariant ? getOfferByVariantId(urlVariant) : null;
      }
      if (!initialOffer) initialOffer = getOfferByKey('1');
      if (initialOffer) selectOffer(initialOffer.offer);
      ensureGuaranteeLine();
      interceptAddToCart();
    }).catch(function (e) {
      console.warn('[offer-bridge] Could not fetch product data:', e);
    });
  }

  function init() {
    boot();
    document.addEventListener('product-info:loaded', function () { state.booted = false; boot(); });
    document.addEventListener('shopify:section:load', function () { state.booted = false; boot(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
